/**
 * Fifi's delivery estimate ("arrives by Wed 19 Nov"), worked out the way Shopify checkout counts it:
 * - business days are Monday to Friday, excluding England & Wales bank holidays;
 * - an order placed after 12:00 UK time (or on a weekend or bank holiday) starts on the next business day;
 * - the date is that start day + processing days + the shipping speed's transit days (Faster Shipping or
 *   Standard), all in business days.
 *
 * Computed in the browser because cached pages can't know today's date. Rendered by
 * snippets/fifi-delivery-estimate.liquid as <fifi-delivery-date>; other modules can import the helpers
 * from '@theme/fifi-delivery'.
 */

const UK_TIME_ZONE = 'Europe/London';
const CUTOFF_HOUR = 12;
const DAY_MS = 86_400_000;
const DEFAULT_PROCESSING_DAYS = 15;
const DEFAULT_TRANSIT_DAYS = 3;

/** @type {Map<number, Set<string>>} */
const bankHolidayCache = new Map();

/**
 * @param {number} year
 * @param {number} month - 0-based.
 * @param {number} day
 * @returns {Date} Midnight UTC, used as a plain calendar date.
 */
function calendarDate(year, month, day) {
  return new Date(Date.UTC(year, month, day));
}

/**
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addDays(date, days) {
  return new Date(date.getTime() + days * DAY_MS);
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
function isoDay(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * Easter Sunday (anonymous Gregorian algorithm).
 * @param {number} year
 * @returns {Date}
 */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return calendarDate(year, month - 1, day);
}

/**
 * @param {number} year
 * @param {number} month - 0-based.
 * @returns {Date} The first Monday of the month.
 */
function firstMonday(year, month) {
  const first = calendarDate(year, month, 1);
  return addDays(first, (8 - first.getUTCDay()) % 7);
}

/**
 * @param {number} year
 * @param {number} month - 0-based.
 * @returns {Date} The last Monday of the month.
 */
function lastMonday(year, month) {
  const last = calendarDate(year, month + 1, 0);
  return addDays(last, -((last.getUTCDay() + 6) % 7));
}

/**
 * England & Wales bank holidays for a year, including substitute days for holidays that fall at a
 * weekend. One-off holidays (e.g. a coronation) are not included.
 * @param {number} year
 * @returns {Set<string>} YYYY-MM-DD dates.
 */
export function englandAndWalesBankHolidays(year) {
  const cached = bankHolidayCache.get(year);
  if (cached) return cached;

  const newYear = calendarDate(year, 0, 1);
  const newYearWeekday = newYear.getUTCDay();
  const easter = easterSunday(year);

  // Christmas Day and Boxing Day, moved to the following weekdays when they fall at a weekend
  let christmas = 25;
  let boxingDay = 26;
  switch (calendarDate(year, 11, 25).getUTCDay()) {
    case 5: // Friday: Boxing Day (Saturday) moves to Monday 28th
      boxingDay = 28;
      break;
    case 6: // Saturday: Monday 27th and Tuesday 28th
      christmas = 27;
      boxingDay = 28;
      break;
    case 0: // Sunday: Boxing Day stays Monday 26th, Christmas moves to Tuesday 27th
      christmas = 27;
      break;
  }

  const holidays = [
    newYearWeekday === 6 ? addDays(newYear, 2) : newYearWeekday === 0 ? addDays(newYear, 1) : newYear,
    addDays(easter, -2), // Good Friday
    addDays(easter, 1), // Easter Monday
    firstMonday(year, 4), // Early May bank holiday
    lastMonday(year, 4), // Spring bank holiday
    lastMonday(year, 7), // Summer bank holiday
    calendarDate(year, 11, christmas),
    calendarDate(year, 11, boxingDay),
  ];

  const set = new Set(holidays.map(isoDay));
  bankHolidayCache.set(year, set);
  return set;
}

/**
 * @param {Date} date - A calendar date (midnight UTC).
 * @returns {boolean} Monday to Friday and not an England & Wales bank holiday.
 */
export function isBusinessDay(date) {
  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return !englandAndWalesBankHolidays(date.getUTCFullYear()).has(isoDay(date));
}

/**
 * @param {Date} date
 * @returns {Date} The next business day after `date`.
 */
function nextBusinessDay(date) {
  let day = addDays(date, 1);
  while (!isBusinessDay(day)) day = addDays(day, 1);
  return day;
}

/**
 * Today's date and hour in the UK (handles GMT/BST).
 * @param {Date} now
 * @returns {{ date: Date, hour: number }}
 */
function ukToday(now) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  /** @param {string} type */
  const part = (type) => Number(parts.find((entry) => entry.type === type)?.value);

  return { date: calendarDate(part('year'), part('month') - 1, part('day')), hour: part('hour') };
}

/**
 * The latest expected delivery date for an order placed `now`.
 * @param {object} [options]
 * @param {number} [options.processingDays] - Business days to make the order. Defaults to 15.
 * @param {number} [options.transitDays] - Business days in transit for the shipping speed. Defaults to 3.
 * @param {Date} [options.now] - When the order is placed. Defaults to now.
 * @returns {Date} A calendar date (midnight UTC).
 */
export function estimateDeliveryDate({
  processingDays = DEFAULT_PROCESSING_DAYS,
  transitDays = DEFAULT_TRANSIT_DAYS,
  now = new Date(),
} = {}) {
  const today = ukToday(now);
  let day = isBusinessDay(today.date) && today.hour < CUTOFF_HOUR ? today.date : nextBusinessDay(today.date);

  for (let count = 0; count < processingDays + transitDays; count += 1) {
    day = nextBusinessDay(day);
  }

  return day;
}

/**
 * Formats a delivery date as "Wed 19 Nov" using the page's language for the day and month names.
 * @param {Date} date
 * @returns {string}
 */
export function formatDeliveryDate(date) {
  const parts = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).formatToParts(date);
  /** @param {string} type */
  const part = (type) => parts.find((entry) => entry.type === type)?.value ?? '';

  return `${part('weekday')} ${part('day')} ${part('month')}`;
}

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function parseDays(value, fallback) {
  const number = Number(value);
  return value === undefined || value.trim() === '' || !Number.isFinite(number) ? fallback : number;
}

/**
 * <fifi-delivery-date data-processing-days="15" data-transit-days="3"></fifi-delivery-date>
 * Shows the estimated delivery date. The text lives in a shadow root so Horizon's morphing of cart
 * markup can't wipe it.
 */
class FifiDeliveryDate extends HTMLElement {
  connectedCallback() {
    const date = estimateDeliveryDate({
      processingDays: parseDays(this.dataset.processingDays, DEFAULT_PROCESSING_DAYS),
      transitDays: parseDays(this.dataset.transitDays, DEFAULT_TRANSIT_DAYS),
    });
    const root = this.shadowRoot ?? this.attachShadow({ mode: 'open' });
    root.textContent = formatDeliveryDate(date);
  }
}

if (!customElements.get('fifi-delivery-date')) {
  customElements.define('fifi-delivery-date', FifiDeliveryDate);
}
